import React, { useCallback, useEffect, useState } from 'react';
import {
  Dimmer,
  Dropdown,
  Form,
  Grid,
  Icon,
  Input,
  Loader,
  Popup,
  Sticky
} from 'semantic-ui-react';
import Button from '../../../../../components/Button';
import IconButton from '../../../../../components/IconButton';
import { connect, useSelector } from 'react-redux';
import { Formik, FormikValues, FormikActions } from 'formik';
import * as Yup from 'yup';
import * as S from './styles';
import { FieldLabel } from '../../../../../styles';
import AppMaskedInput from '../../../../../components/MaskedInput';
import CustomDatePicker from '../../../../../components/CustomDatePicker';
import { iRootDispatch, iRootState } from '../../../../../store';
import { CurrentPageState } from '../../../../../store/types/temp-types';
import { warningNotification } from '../../../../../services/notification';
import { DATE_INPUT_MASK, numberToMoneyString } from '../../../../../services/masks';
import { get, post } from '../../../../../services/request';
import YelumCreatePolicyModal from './components/CreatePolicyModal';
import RangeSlider from './components/range-slider';
import {
  LoadCoveragesResponse,
  LoadCoveragesResponseControl
} from './interfaces/yelum-coverages.interface';
import { YelumBudgetFormProps } from './interfaces/yelum.budget.interface';
import { useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { UserRolesEnum } from '../../../../../enums/user-roles.enum';
import { UserState } from '../../../../../types/UserState';
import AssistanceAndCoverageDetailsModal from './components/AssistanceAndCoverageDetailsModal';
import {
  applyYelumCompetenceToFormValues,
  buildYelumCompetenceOptions,
  getCompetenceFlowLabel,
  inferYelumCompetenceFromDate,
  YelumCompetence,
} from '../../../../../utils/yelumCompetence';

const defaultCompetenceFormValues = applyYelumCompetenceToFormValues(
  YelumCompetence.CURRENT_MONTH,
);


interface YelumCalculationResponse {
  netPremiumAnnual: number;
  iofAnnual: number;
  totalAnnual: number;
  netPremiumMonthly: number;
  iofMonthly: number;
  totalMonthly: number;
  details: {
    regiao: string;
    cepData: {
      code: number;
      macro: string;
      microRegionDescription: string;
    };
    vigencia: {
      dias: number;
      factorProRata: number;
    };
    coberturas: Array<{
      cobertura: string;
      valorCobertura: number;
      taxaLiquida: number;
      taxaComComissao: number;
      premioLiquido: number;
      premioBruto: number;
    }>;
    assistencia: {
      tipo: string;
      valorAnual: number;
      valorMensal: number;
    };
    impostos: {
      iofRate: number;
      iofAnual: number;
      iofMensal: number;
    };
  };
}

interface CustomDropDownOptions {
  value: any;
  key: number | string;
  text: string;
}

export enum BudgetContext {
  new = 'NEW',
  detail = 'DETAIL',
  editing = 'EDITING',
  renewal = 'RENEWAL',
  copyCanceled = 'COPY_CANCELED',
  bondInsurance = 'BOND_INSURANCE'
}

const YelumSeguroBudgetForm = (props: YelumBudgetFormProps) => {
  const {
    estate,
    insurerId,
    context,
    policyOrigin,
    budgetId,
    initialRentValue,
    initialStartDate,
    onBudgetCreated,
    isIframe,
    initialAddress,
    initialTenant,
    initialInsured
  } = props;

  const location = useLocation();
  const isEdit = [BudgetContext.detail, BudgetContext.editing].includes(context);
  const user = useSelector((state: iRootState) => state.user);
  const canContract = user.role !== UserRolesEnum.estateQuotation;
  const canEditPl =
    user.role !== UserRolesEnum.estateQuotation &&
    (user.role !== UserRolesEnum.estateUser ||
      Boolean(estate.allowEstateUserPlEdit));
  const [openCreatePolicyModal, setOpenCreatePolicyModal] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0); //isEdit ? 1 : 0
  const [hasDiscount, setHasDiscount] = useState<boolean>(false);
  const [coverages, setCoverages] = useState<LoadCoveragesResponseControl[]>([]);
  const [configs, setConfigs] = useState<any>({});
  const [loadingInitialValues, setLoadingInitialValues] = useState<boolean>(true);
  const [grossPremium, setGrossPremium] = useState<number>();
  const [data, setData] = useState<any>();
  const [initialValues, setInitialValues] = useState<any>({
    installments: 12,
    coverageType: 'Prédio e Conteúdo',
    fireInsuranceValue: 0,
    coverages: [],
    brokerCommission: 0,
    competence: defaultCompetenceFormValues.competence,
    startVigency: defaultCompetenceFormValues.startVigency,
    endVigency: defaultCompetenceFormValues.endVigency,
  });
  const [competenceOptions] = useState(() => buildYelumCompetenceOptions().options);
  const [savedData, setSavedData] = useState<any>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [budgetLoadedData, setBudgetLoadedData] = useState<any>(null);

  const [allCoverages, setAllCoverages] = useState<LoadCoveragesResponse[]>([]);
  const [showAllCoverages, setShowAllCoverages] = useState<boolean>(false);
  const [calculationResult, setCalculationResult] =
    useState<YelumCalculationResponse | null>(null);
  const [isConfiguracoesCoberturaExpanded, setIsConfiguracoesCoberturaExpanded] =
    useState<boolean>(false);
  const [assistanceCoverages, setAssistanceCoverages] = useState<LoadCoveragesResponse[]>(
    []
  );
  const [fireCoverages, setFireCoverages] = useState<LoadCoveragesResponse[]>([]);
  const [selectedAssistance, setSelectedAssistance] = useState<string | null>(null);
  const [hasYelumPolicies, setHasYelumPolicies] = useState<boolean>(false);
  const [showAssistanceCoverageInfoModal, setShowAssistanceCoverageInfoModal] =
    useState<boolean>(false);
  const [typeAssistanceCoverageModal, setTypeAssistanceCoverageModal] = useState<
    'assistance' | 'coverage'
  >('assistance');

  // Lender Insurance
  const lenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );
  const lenderInsuranceRate = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceRate
  );
  const [includeLenderInsurance, setIncludeLenderInsurance] = useState<boolean>(false);
  // Opções de parcelas: se flexibleInstallmentsEnabled estiver ativo, mostra 1x-12x
  // Caso contrário, mostra apenas 12x fixo
  const allInstallmentsOptions = [
    { key: 1, text: '1x', value: 1 },
    { key: 2, text: '2x', value: 2 },
    { key: 3, text: '3x', value: 3 },
    { key: 4, text: '4x', value: 4 },
    { key: 5, text: '5x', value: 5 },
    { key: 6, text: '6x', value: 6 },
    { key: 7, text: '7x', value: 7 },
    { key: 8, text: '8x', value: 8 },
    { key: 9, text: '9x', value: 9 },
    { key: 10, text: '10x', value: 10 },
    { key: 11, text: '11x', value: 11 },
    { key: 12, text: '12x', value: 12 }
  ];

  const fixedInstallmentsOptions = [{ key: 12, text: '12x', value: 12 }];

  const installmentsOptions = configs?.flexibleInstallmentsEnabled
    ? allInstallmentsOptions
    : fixedInstallmentsOptions;

  const buildingContentOptions = [
    // {
    //   key: 1,
    //   text: 'Prédio',
    //   value: 'Prédio'
    // },
    // {
    //   key: 2,
    //   text: 'Conteúdo',
    //   value: 'Conteúdo'
    // },
    {
      key: 3,
      text: 'Prédio e Conteúdo',
      value: 'Prédio e Conteúdo'
    }
  ];
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<CustomDropDownOptions[]>(
    [
      {
        key: 7,
        text: 'Apartamento',
        value: 'Apartamento'
      },
      {
        key: 8,
        text: 'Residência Alvenaria',
        value: 'Residência Alvenaria'
      },
      {
        key: 1,
        text: 'Escritório e Consultório',
        value: 'Escritório e Consultório'
      },
      {
        key: 3,
        text: 'Comércio e Serviço',
        value: 'Comércio e Serviço'
      },
      {
        key: 9,
        text: 'Casa Madeira',
        value: 'Casa Madeira'
      }
    ]
  );

  useEffect(() => {
    if (context === BudgetContext.bondInsurance) {
      console.log(
        'Contexto seguro fiança detectado, garantindo que loading não fique preso'
      );

      const timer = setTimeout(() => {
        if (loadingInitialValues) {
          setLoadingInitialValues(false);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [context, loadingInitialValues]);

  // Verificar se imobiliária já tem apólices Yelum emitidas
  useEffect(() => {
    const checkEstateHasPolicies = async () => {
      if (!estate?.id) return;

      try {
        const response = await get(
          `/fire-insurance/yelum/estate/${estate.id}/has-policies`
        );
        setHasYelumPolicies(response.hasPolicies || false);
      } catch (error) {
        console.error('Erro ao verificar apólices da imobiliária:', error);
        // Em caso de erro, assumir que não tem apólices (mais restritivo)
        setHasYelumPolicies(false);
      }
    };

    checkEstateHasPolicies();
  }, [estate?.id]);

  function hasChange() {
    if (step === 1) {
      setHasDiscount(false);
      setStep(0);
    }
  }

  useEffect(() => {
    hasChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estate]);

  const getFireCoverageByType = (coverageType: string, propertyType?: string) => {
    const codeMap: { [key: string]: string } = {
      'Prédio e Conteúdo': 'E10',
      Prédio: 'E11',
      Conteúdo: 'E12'
    };

    const codeSuffix = codeMap[coverageType];
    return fireCoverages.find(fc => {
      if (!fc.coverage.code) return false;
      const codeParts = fc.coverage.code.split('-');
      const codeMatches = codeParts.length > 1 && codeParts[1] === codeSuffix;

      // Se propertyType foi fornecido, verificar se a cobertura é do tipo correto
      if (propertyType && fc.propertyType) {
        return codeMatches && fc.propertyType === propertyType;
      }

      return codeMatches;
    });
  };

  const handleSubmit = async (
    formValues: FormikValues,
    { setSubmitting }: FormikActions<FormikValues>
  ) => {
    try {
      const selectedCoverages = formValues.coverages
        .filter((cvControl: LoadCoveragesResponseControl) => cvControl.checked)
        .map((cvControl: LoadCoveragesResponseControl) => ({
          id: cvControl.coverage.id,
          value: cvControl.value
        }));

      // Adicionar a cobertura de incêndio correta baseada no tipo selecionado
      const fireCoverage = getFireCoverageByType(
        formValues.coverageType,
        formValues.propertyType
      );
      if (fireCoverage && formValues.fireInsuranceValue) {
        selectedCoverages.unshift({
          id: fireCoverage.coverage.id,
          value: formValues.fireInsuranceValue
        });
      }

      // Determinar tipo de assistência para o backend
      let assistanceType = 'Sem Assistência';
      console.log('[handleSubmit] selectedAssistance:', selectedAssistance);
      console.log('[handleSubmit] assistanceCoverages:', assistanceCoverages.length);

      if (selectedAssistance && selectedAssistance !== '' && selectedAssistance !== '-') {
        const assistance = assistanceCoverages.find(
          a => String(a.coverage.id) === String(selectedAssistance)
        );
        console.log('[handleSubmit] Assistência encontrada:', assistance);

        if (assistance) {
          const assistanceName = assistance.coverage.name.toLowerCase();
          if (assistanceName.includes('completo')) {
            assistanceType = 'Completa';
          } else if (
            assistanceName.includes('basico') ||
            assistanceName.includes('básico')
          ) {
            assistanceType = 'Básica';
          } else {
            assistanceType = 'Básica'; // Padrão para outras assistências
          }

          selectedCoverages.push({
            id: assistance.coverage.id,
            value: 0 // Assistências não têm valor variável
          });
          console.log(
            '[handleSubmit] Assistência adicionada às coberturas:',
            assistance.coverage.name
          );
        } else {
          console.warn(
            '[handleSubmit] Assistência não encontrada no array assistanceCoverages'
          );
        }
      } else {
        console.log('[handleSubmit] Nenhuma assistência selecionada ou valor inválido');
      }

      const calculationPayload = {
        estateId: estate.id,
        insurerId: insurerId,
        data: {
          rentValue: formValues.rentValue,
          competence: formValues.competence,
          policyPeriodStart: formValues.startVigency,
          policyPeriodEnd: formValues.endVigency,
          propertyType: formValues.propertyType,
          coverageType: formValues.coverageType,
          commissionTax: Number(formValues.commissionTax),
          brokerCommission: Number(formValues.brokerCommission),
          assistanceType: assistanceType,
          coverages: selectedCoverages
        }
      };

      const calculationResponse: YelumCalculationResponse = await post(
        '/fire-insurance-quotations/yelum-seguros/quote',
        calculationPayload
      );
      setCalculationResult(calculationResponse);
      setGrossPremium(calculationResponse.totalAnnual);
      setData({
        ...calculationResponse,
        maxPremium: calculationResponse.totalAnnual,
        totalPremium: calculationResponse.totalAnnual
      });
      setStep(1);
    } catch (error) {
      console.log(error);
      warningNotification(
        'Erro ao calcular orçamento de seguro incêndio',
        'Não foi possível calcular o prêmio com os dados fornecidos. Verifique se todos os campos estão preenchidos corretamente.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    propertyType: Yup.string().test('propertyType', 'error', value => {
      if (!value) {
        warningNotification(
          'O campo Tipo do Imóvel é obrigatório',
          'Por favor, preencha o campo corretamente.'
        );
        return false;
      }

      return true;
    }),
    coverageType: Yup.string().test('coverageType', 'error', value => {
      if (!value) {
        warningNotification(
          'O campo Tipo de Cobertura a é obrigatório',
          'Por favor, preencha o campo corretamente.'
        );
        return false;
      }

      return true;
    }),
    competence: Yup.string().test('competence', 'error', value => {
      if (!value) {
        warningNotification(
          'O campo Competência é obrigatório',
          'Por favor, selecione a competência do seguro.'
        );
        return false;
      }

      return true;
    }),
    endVigency: Yup.string().test('endVigency', 'error', value => {
      if (!value) {
        warningNotification(
          'O campo Fim de Vigência é obrigatório',
          'Por favor, preencha o campo corretamente.'
        );
        return false;
      }

      return true;
    }),

    rentValue: Yup.string().test('rentValue', 'error', value => {
      if (Number.isNaN(Number(value)) || value === '0') {
        warningNotification(
          'O campo Valor do Aluguel é obrigatório',
          'Por favor, preencha o campo corretamente.'
        );
        return false;
      }

      return true;
    }),
    fireInsuranceValue: Yup.string().test('fireInsuranceValue', 'error', value => {
      if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) {
        warningNotification(
          'O campo Cobertura de Incêndio é obrigatório',
          'Por favor, preencha o valor da cobertura de incêndio.'
        );
        return false;
      }

      return true;
    })
  });

  const loadCoverages = useCallback(async () => {
    console.log('[Yelum] Iniciando carregamento de coberturas...');
    try {
      const response: LoadCoveragesResponse[] = await get(
        `/alfa-seguros/coverage/coverage-estate/${estate.id}?insurerId=${insurerId}`
      );

      setAllCoverages(response);
    } catch (error) {
      console.error('[Yelum] Erro ao carregar coberturas:', error);
    }
  }, [estate.id, insurerId]);

  const createCoveragesWithDefaults = useCallback(
    (
      coveragesList: LoadCoveragesResponse[],
      rentValue = 0
    ): LoadCoveragesResponseControl[] => {
      return coveragesList.map(c => {
        let value = 0;

        // Prioridade: defaultValue da estate > defaultValue global > multiplier * rentValue
        const defaultValue = c.defaultValue ?? c.coverage?.defaultValue;
        const defaultMultiplier =
          c.defaultMultiplier ?? c.multiplier ?? c.coverage?.defaultMultiplier;
        const defaultEnabled =
          c.defaultEnabled ?? c.coverage?.defaultEnabled ?? c.isRequired;

        if (defaultValue && defaultValue > 0) {
          // Usar valor fixo padrão (em reais)
          value = defaultValue;
        } else if (defaultMultiplier && defaultMultiplier > 0 && rentValue > 0) {
          // Usar multiplicador * aluguel
          value = defaultMultiplier * rentValue;
        } else if (c.multiplier && c.multiplier > 0 && rentValue > 0) {
          // Fallback para multiplier da configuração da imobiliária
          value = c.multiplier * rentValue;
        }

        // Respeitar limite máximo
        if (c.maxValue && value > c.maxValue) {
          value = c.maxValue;
        }

        // Respeitar limite mínimo
        if (c.minValue && value < c.minValue) {
          value = c.minValue;
        }

        return {
          ...c,
          checked: defaultEnabled,
          onEdit: false,
          value: value,
          onEditValue: value
        };
      });
    },
    []
  );

  const filterCoveragesByPropertyType = useCallback(
    (propertyType: string | null) => {
      if (!propertyType || allCoverages.length === 0) {
        console.warn(
          `[Yelum] Não foi possível filtrar coberturas - propertyType: ${propertyType}, allCoverages: ${allCoverages.length}`
        );
        return;
      }

      // Filtrar assistências por tipo de imóvel
      const assistances = allCoverages.filter(c => {
        if (!c.coverage.label?.toUpperCase().includes('ASSISTENCIA') || c.propertyType) {
          return false;
        }

        const label = c.coverage.label;

        // Para Apartamento, Residência Alvenaria e Casa Madeira: apenas labels com 11054
        if (
          ['Apartamento', 'Residência Alvenaria', 'Casa Madeira'].includes(propertyType)
        ) {
          return label.includes('11054');
        }

        // Para Escritório e Consultório, Comércio e Serviço: apenas labels com 11112
        if (['Escritório e Consultório', 'Comércio e Serviço'].includes(propertyType)) {
          return label.includes('11112');
        }

        return false;
      });

      // Coberturas de incêndio filtradas por propertyType
      const fires = allCoverages.filter(
        c =>
          c.coverage.label?.toUpperCase().includes('INCENDIO') &&
          ['E10', 'E11', 'E12'].includes(c.coverage.code?.split('-')[1]) &&
          c.propertyType === propertyType
      );

      // Coberturas regulares filtradas por propertyType
      const otherCoverages = allCoverages.filter(
        c =>
          !c.coverage.label?.toUpperCase().includes('ASSISTENCIA') &&
          !(
            c.coverage.label?.toUpperCase().includes('INCENDIO') &&
            ['E10', 'E11', 'E12'].includes(c.coverage.code?.split('-')[1])
          ) &&
          c.propertyType === propertyType
      );

      // Remover duplicidades das assistências usando coverage.id como chave única
      const uniqueAssistances = assistances.reduce((acc, current) => {
        const exists = acc.find(item => item.coverage.id === current.coverage.id);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, [] as LoadCoveragesResponse[]);

      console.log(
        `[Yelum] Coberturas filtradas - Assistências: ${uniqueAssistances.length} (${assistances.length} antes de remover duplicatas), Incêndio: ${fires.length}, Outras: ${otherCoverages.length}`
      );
      console.log(
        '[Yelum] Assistências únicas:',
        uniqueAssistances.map(a => ({
          id: a.coverage.id,
          name: a.coverage.name,
          label: a.coverage.label
        }))
      );

      setAssistanceCoverages(uniqueAssistances);
      setFireCoverages(fires);

      const cov = createCoveragesWithDefaults(otherCoverages);
      setCoverages(cov);

      return cov;
    },
    [allCoverages, createCoveragesWithDefaults]
  );

  const loadConfigs = useCallback(async () => {
    const configs = await get(`/estates/fire-active-insurer/${estate.id}/${insurerId}`);
    if (configs) {
      setInitialValues((prev: any) => ({
        ...prev,
        commissionTax: configs?.compensation || 0,
        brokerCommission: configs?.brokerCommission || 0,
        fireInsuranceValue: prev.fireInsuranceValue || 0
      }));
    }

    setConfigs(configs);
  }, [estate.id, insurerId]);

  async function loadDataForEditingOrCopy() {
    setLoadingInitialValues(true);

    const budgetId = location?.pathname?.replace('/admin/fire-insurance-budget/', '');

    if (!budgetId) {
      warningNotification(
        'Erro ao carregar os dados do orçamento',
        'Se o erro persistir contacte o suporte'
      );
      return;
    }

    try {
      const data = await get(`/fire-insurance-quotations/budget/${budgetId}`);

      const { fireInsurance, coverages: cvrgs } = data;
      const { propertyAddresses: property } = fireInsurance;
      let savedAssistance: string | null = null;
      let savedFireInsuranceValue = 0;

      // Filtrar coberturas regulares (sem assistência e sem incêndio) pelo propertyType
      const regularCoverages = allCoverages.filter(
        c =>
          !c.coverage.label?.toUpperCase().includes('ASSISTENCIA') &&
          !(
            c.coverage.label?.toUpperCase().includes('INCENDIO') &&
            ['E10', 'E11', 'E12'].includes(c.coverage.code?.split('-')[1])
          ) &&
          c.propertyType === fireInsurance.occupation
      );

      // IMPORTANTE: referenceCode salvo no banco é o coverage.code (ex: "11404-R04"), NÃO o coverage.id (UUID)
      const selectedCoverages: LoadCoveragesResponseControl[] = regularCoverages.map(
        (loadedCoverage: LoadCoveragesResponse) => {
          const coverageSaved = cvrgs.find(
            (c: any) => String(loadedCoverage.coverage.code) === String(c.referenceCode)
          );
          if (coverageSaved) {
            return {
              ...loadedCoverage,
              checked: true,
              value: coverageSaved.value ? +Number(coverageSaved.value).toFixed(2) : 0,
              onEdit: false,
              onEditValue: 0
            };
          }

          let value = 0;
          const defaultValue = loadedCoverage.defaultValue ?? loadedCoverage.coverage?.defaultValue;
          const defaultMultiplier = loadedCoverage.defaultMultiplier ?? loadedCoverage.multiplier ?? loadedCoverage.coverage?.defaultMultiplier;
          const defaultEnabled = loadedCoverage.defaultEnabled ?? loadedCoverage.coverage?.defaultEnabled ?? loadedCoverage.isRequired;

          if (defaultValue && defaultValue > 0) {
            value = defaultValue;
          } else if (defaultMultiplier && defaultMultiplier > 0 && +data.fireInsurance.rentValue > 0) {
            value = defaultMultiplier * +data.fireInsurance.rentValue;
          } else if (loadedCoverage.multiplier && loadedCoverage.multiplier > 0 && +data.fireInsurance.rentValue > 0) {
            value = loadedCoverage.multiplier * +data.fireInsurance.rentValue;
          }

          if (loadedCoverage.maxValue && value > loadedCoverage.maxValue) value = loadedCoverage.maxValue;
          if (loadedCoverage.minValue && value < loadedCoverage.minValue) value = loadedCoverage.minValue;

          return {
            ...loadedCoverage,
            checked: defaultEnabled || false,
            onEdit: false,
            value: +Number(value).toFixed(2),
            onEditValue: 0
          };
        }
      );

      // Buscar assistência salva - comparar pelo coverage.code, não coverage.id
      const assistanceSaved = cvrgs.find((c: any) => {
        const assistCoverage = allCoverages.find(
          ac =>
            String(ac.coverage.code) === String(c.referenceCode) &&
            (ac.coverage.name.toUpperCase().includes('ASSISTENCIA') ||
              ac.coverage.label?.toUpperCase().includes('ASSISTENCIA'))
        );
        return !!assistCoverage;
      });

      console.log('[Yelum Edit] Assistência salva encontrada:', assistanceSaved ? {
        referenceCode: assistanceSaved.referenceCode,
        coverageName: assistanceSaved.coverageName
      } : 'NÃO ENCONTRADA');

      if (assistanceSaved) {
        // Encontrar o coverage correspondente em allCoverages para obter o coverage.id (usado no dropdown)
        const matchingAssistCoverage = allCoverages.find(
          ac => String(ac.coverage.code) === String(assistanceSaved.referenceCode) &&
            (ac.coverage.name.toUpperCase().includes('ASSISTENCIA') ||
              ac.coverage.label?.toUpperCase().includes('ASSISTENCIA'))
        );
        if (matchingAssistCoverage) {
          // O dropdown usa coverage.id como value, então setamos o coverage.id
          savedAssistance = String(matchingAssistCoverage.coverage.id);
        } else {
          console.warn('[Yelum Edit] Assistência encontrada no orçamento mas não encontrada em allCoverages');
          console.warn('Assistência encontrada no orçamento:', assistanceSaved);
          console.warn('Assistência encontrada em allCoverages:', matchingAssistCoverage);
        }
      }

      // Buscar cobertura de incêndio salva - comparar pelo coverage.code
      const fireSaved = cvrgs.find((c: any) => {
        const fireCoverage = allCoverages.find(
          fc =>
            String(fc.coverage.code) === String(c.referenceCode) &&
            fc.coverage.label?.toUpperCase().includes('INCENDIO') &&
            ['E10', 'E11', 'E12'].includes(fc.coverage.code?.split('-')[1]) &&
            fc.propertyType === fireInsurance.occupation
        );
        return !!fireCoverage;
      });

      if (fireSaved) {
        savedFireInsuranceValue = fireSaved.value
          ? +Number(fireSaved.value).toFixed(2)
          : 0;
      }

      const startDate = moment(data.policyPeriodStart).toDate();
      const inferredCompetence = inferYelumCompetenceFromDate(startDate);
      if (!inferredCompetence) {
        warningNotification(
          'Competência ajustada',
          'Este orçamento possui vigência fora das competências disponíveis. Ajustamos para o próximo mês — revise antes de salvar.'
        );
      }
      const resolvedCompetence =
        inferredCompetence ?? YelumCompetence.NEXT_MONTH;
      const competenceDates = applyYelumCompetenceToFormValues(resolvedCompetence);

      // data.assistance contém o coverageType (ex: "Prédio e Conteúdo"), NÃO a assistência real
      const normalizeData = {
        // Step 1
        propertyType: fireInsurance.occupation,
        coverageType: data.coverageType || data.assistance || 'Prédio e Conteúdo',
        competence: resolvedCompetence,
        startVigency: competenceDates.startVigency,
        endVigency: competenceDates.endVigency,
        rentValue: +fireInsurance.rentValue,
        commissionTax: data.estateComissionPercentage
          ? Number(data.estateComissionPercentage).toFixed(2)
          : 0,
        brokerCommission: data.brokerCommission
          ? Number(data.brokerCommission).toFixed(2)
          : 0,
        fireInsuranceValue: savedFireInsuranceValue,
        // Step 2
        propertyOwnerName: fireInsurance.propertyOwnerName,
        propertyOwnerDocument: fireInsurance.propertyOwnerDocument,
        propertyOwnerCellphone: fireInsurance.propertyOwnerCellphone,
        tenantName: fireInsurance.tenantName,
        tenantDocument: fireInsurance.tenantDocument,
        tenantCellphone: fireInsurance.tenantCellphone,
        zipCode: property.zipcode,
        city: property.city,
        state: property.state,
        street: property.street,
        district: property.district,
        number: property.number,
        complement: property.complement,
        reference: (fireInsurance as any).propertyExternalCode || '',
        // Ordenar: coberturas pré-assinaladas (checked) primeiro
        coverages: [...selectedCoverages].sort((a, b) => {
          if (a.checked && !b.checked) return -1;
          if (!a.checked && b.checked) return 1;
          return 0;
        })
      };

      setInitialValues(normalizeData);
      setCoverages(normalizeData.coverages);
      setSelectedAssistance(savedAssistance);

      // Atualizar também as coberturas de assistências e incêndio filtradas por propertyType
      const propertyTypeForFilter = fireInsurance.occupation;
      const assistances = allCoverages.filter(c => {
        if (!c.coverage.label?.toUpperCase().includes('ASSISTENCIA') || c.propertyType) {
          return false;
        }

        const label = c.coverage.label;

        // Para Apartamento, Residência Alvenaria e Casa Madeira: apenas labels com 11054
        if (
          ['Apartamento', 'Residência Alvenaria', 'Casa Madeira'].includes(
            propertyTypeForFilter
          )
        ) {
          return label.includes('11054');
        }

        // Para Escritório e Consultório, Comércio e Serviço: apenas labels com 11112
        if (
          ['Escritório e Consultório', 'Comércio e Serviço'].includes(
            propertyTypeForFilter
          )
        ) {
          return label.includes('11112');
        }

        return false;
      });

      const fires = allCoverages.filter(
        c =>
          c.coverage.label?.toUpperCase().includes('INCENDIO') &&
          ['E10', 'E11', 'E12'].includes(c.coverage.code?.split('-')[1]) &&
          c.propertyType === fireInsurance.occupation
      );

      // Remover duplicidades das assistências usando coverage.id como chave única
      const uniqueAssistances = assistances.reduce((acc, current) => {
        const exists = acc.find(item => item.coverage.id === current.coverage.id);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, [] as LoadCoveragesResponse[]);

      setAssistanceCoverages(uniqueAssistances);
      setFireCoverages(fires);

      // Salvar dados carregados para uso no modal
      const budgetData = {
        ...normalizeData,
        // Dados originais salvos
        budgetId: data.id,
        fireInsuranceId: fireInsurance.id,
        savedRentValue: fireInsurance.rentValue,
        savedPropertyType: fireInsurance.occupation,
        savedTenantName: fireInsurance.tenantName,
        savedTenantDocument: fireInsurance.tenantDocument,
        savedTenantCellphone: fireInsurance.tenantCellphone,
        savedPropertyOwnerName: fireInsurance.propertyOwnerName,
        savedPropertyOwnerDocument: fireInsurance.propertyOwnerDocument,
        savedPropertyOwnerCellphone: fireInsurance.propertyOwnerCellphone,
        savedCoverages: selectedCoverages,
        savedProperty: property,
        policyPeriodStart: moment(data.policyPeriodStart).format('DD/MM/YYYY'),
        policyPeriodEnd: moment(data.policyPeriodEnd).format('DD/MM/YYYY'),
        // Dados de cotação (se existir)
        grossPremium: data.grossPremium,
        value: data.value,
        installments: data.installments
      };

      setBudgetLoadedData(budgetData);
    } catch (error) {
      console.log('Erro ao carregar dados Yelum:', error);

      warningNotification(
        'Erro ao carregar os dados do orçamento',
        'Se o erro persistir contacte o suporte'
      );
    } finally {
      setLoadingInitialValues(false);
    }
  }

  useEffect(() => {
    loadCoverages();
    loadConfigs();
  }, [context, loadCoverages, loadConfigs]);

  /**
   * Recarrega coberturas quando allCoverages são carregadas e há um propertyType definido
   */
  useEffect(() => {
    if (allCoverages.length > 0) {
      console.log('[Yelum] Coberturas carregadas do backend:', allCoverages.length);

      const isEditingOrCopy = [BudgetContext.editing, BudgetContext.copyCanceled, BudgetContext.renewal].includes(context);

      if (!isEditingOrCopy) {
        if (initialValues.propertyType) {
          filterCoveragesByPropertyType(initialValues.propertyType);
        } else {
          // Limpar estados até que o usuário selecione um tipo
          setCoverages([]);
          setAssistanceCoverages([]);
          setFireCoverages([]);
        }
      }
    }
  }, [allCoverages, initialValues.propertyType, filterCoveragesByPropertyType, context]);

  useEffect(() => {
    if (initialValues && loadingInitialValues && allCoverages.length > 0) {
      switch (context) {
        case BudgetContext.editing:
        case BudgetContext.copyCanceled:
          loadDataForEditingOrCopy();
          break;
        default:
          setLoadingInitialValues(false);
          break;
      }
    }
  }, [initialValues, loadingInitialValues, allCoverages.length, context]);

  const getCoverageLabelFromCoverage = (coverage: any): string | null => {
    if (!coverage || !coverage.label) {
      return null;
    }
    const label = coverage.label;
    const labelWithoutSuffix = label.replace(/_\d+$/, '');
    return labelWithoutSuffix;
  };

  // Função para buscar limite usando LIKE nas primeiras palavras
  const getLimitByCoverageLike = (
    coverageLabel: string,
    propertyLimits: Record<string, number>
  ): number | undefined => {
    const upper = coverageLabel.toUpperCase();

    // Roubo: distinguir arrombamento vs vestígios (mesmo prefixo ROUBO_E)
    if (upper.includes('ARROMBAMENTO')) {
      return propertyLimits.ROUBO_E_OU_SUBTRACAO_DE_BENS_MEDIANTE_ARROMBAMENTO_VERBA_UNICA;
    }
    if (upper.includes('VESTIGIOS')) {
      return propertyLimits.ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA;
    }

    // Se começar com INCENDIO, usar apenas a primeira palavra
    if (coverageLabel.startsWith('INCENDIO')) {
      const searchPattern = 'INCENDIO';
      const matchingKey = Object.keys(propertyLimits).find(key =>
        key.startsWith(searchPattern)
      );
      return matchingKey ? propertyLimits[matchingKey] : undefined;
    }

    // Para os demais, usar as 2 primeiras palavras
    const words = coverageLabel.split('_');
    if (words.length >= 2) {
      const searchPattern = `${words[0]}_${words[1]}`;
      const matchingKey = Object.keys(propertyLimits).find(key =>
        key.startsWith(searchPattern)
      );
      return matchingKey ? propertyLimits[matchingKey] : undefined;
    }

    // Fallback: busca exata
    return propertyLimits[coverageLabel];
  };

  // Limites máximos de LMI por tipo de imóvel (Yelum)
  const YELUM_PROPERTY_LIMITS: Record<string, Record<string, number>> = {
    Apartamento: {
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
      DANOS_ELETRICOS_VERBA_UNICA: 125000,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
      VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
      PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
      RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
      IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
      QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
      ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000,
      // DANOS_CAUSADOS_AOS_BENS_DO_CONDOMINIO_VERBA_UNICA: 10000
    },
    'Residência Alvenaria': {
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
      DANOS_ELETRICOS_VERBA_UNICA: 125000,
      // DANOS_CAUSADOS_AOS_BENS_DO_CONDOMINIO_VERBA_UNICA: 10000,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
      VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
      PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
      RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
      IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
      QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
      ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
    },
    'Casa Madeira': {
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 375000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 375000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 375000,
      DANOS_ELETRICOS_VERBA_UNICA: 30000,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 30000,
      VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 30000,
      PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 30000,
      RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 30000,
      IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 30000,
      QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 20000,
      ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000.01
    },
    'Escritório e Consultório': {
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
      DANOS_ELETRICOS_VERBA_UNICA: 62500,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_MEDIANTE_ARROMBAMENTO_VERBA_UNICA: 50000,
      VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
      PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
      RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
      QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
      ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
    },
    'Comércio e Serviço': {
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 1000000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 207000,
      INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 1000000,
      DANOS_ELETRICOS_VERBA_UNICA: 62500,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
      ROUBO_E_OU_SUBTRACAO_DE_BENS_MEDIANTE_ARROMBAMENTO_VERBA_UNICA: 50000,
      VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
      PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 80000,
      RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
      QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
      ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 10000
    }
  };

  const coveragesValidation = (
    coverages: LoadCoveragesResponseControl[],
    propertyType: string,
    fireInsuranceValue: number,
    coverageType: string
  ) => {
    if (coverages?.length === 0) {
      return true;
    }

    // Validar valor da cobertura de incêndio
    if (!propertyType) {
      warningNotification(
        'Tipo de imóvel não selecionado',
        'Por favor, selecione o tipo de imóvel antes de criar o orçamento.'
      );
      return false;
    }

    if (!fireInsuranceValue || fireInsuranceValue <= 0) {
      warningNotification(
        'Cobertura de Incêndio obrigatória',
        'Por favor, informe o valor da cobertura de incêndio.'
      );
      return false;
    }

    // Obter limites para o tipo de imóvel
    const propertyLimits = YELUM_PROPERTY_LIMITS[propertyType];
    if (!propertyLimits) {
      console.warn(`Limites não encontrados para tipo de imóvel: ${propertyType}`);
      return true; // Se não houver limites definidos, permite continuar
    }

    // Validar limite da cobertura de incêndio baseado no tipo selecionado
    const fireCoverage = getFireCoverageByType(coverageType, propertyType);
    if (fireCoverage && fireCoverage.coverage) {
      const coverageLabel = getCoverageLabelFromCoverage(fireCoverage.coverage);
      if (coverageLabel) {
        const fireLimit = getLimitByCoverageLike(coverageLabel, propertyLimits);
        if (fireLimit !== undefined && fireInsuranceValue > fireLimit) {
          warningNotification(
            'Valor da cobertura de incêndio excede o limite',
            `O valor da cobertura de incêndio (R$ ${numberToMoneyString(
              fireInsuranceValue
            )}) excede o limite máximo de R$ ${numberToMoneyString(
              fireLimit
            )} para imóveis do tipo "${propertyType}".`
          );
          return false;
        }
      }
    }

    // Validar coberturas regulares
    const checkedCoverages = coverages.filter(c => c.checked);
    const invalidCoverages = checkedCoverages.filter(c => !c.value || c.value <= 0);
    if (invalidCoverages.length > 0) {
      warningNotification(
        'Valores de cobertura inválidos',
        'Todas as coberturas selecionadas devem ter valores maiores que zero.'
      );
      return false;
    }

    // Validar limites individuais de cada cobertura
    for (const coverage of checkedCoverages) {
      if (coverage.coverage) {
        const coverageLabel = getCoverageLabelFromCoverage(coverage.coverage);
        if (coverageLabel) {
          const limit = getLimitByCoverageLike(coverageLabel, propertyLimits);
          if (limit !== undefined && coverage.value > limit) {
            warningNotification(
              'Valor da cobertura excede o limite',
              `O valor da cobertura "${coverage.coverage.name}" (R$ ${numberToMoneyString(
                coverage.value
              )}) excede o limite máximo de R$ ${numberToMoneyString(
                limit
              )} para imóveis do tipo "${propertyType}".`
            );
            return false;
          }
        }
      }
    }

    return true;
  };
  const applyDefaultCoverageValues = (
    coveragesList: LoadCoveragesResponseControl[],
    rentValue: number
  ): LoadCoveragesResponseControl[] => {
    return coveragesList.map(c => {
      let value = 0;

      // Prioridade: defaultValue da estate > defaultValue global > multiplier * rentValue
      const defaultValue = c.defaultValue ?? c.coverage?.defaultValue;
      const defaultMultiplier =
        c.defaultMultiplier ?? c.multiplier ?? c.coverage?.defaultMultiplier;
      const defaultEnabled =
        c.defaultEnabled ?? c.coverage?.defaultEnabled ?? c.isRequired;

      if (defaultValue && defaultValue > 0) {
        value = defaultValue;
      } else if (defaultMultiplier && defaultMultiplier > 0) {
        value = defaultMultiplier * rentValue;
      } else if (c.multiplier && c.multiplier > 0) {
        value = c.multiplier * rentValue;
      }

      // Respeitar limite máximo
      if (c.maxValue && value > c.maxValue) {
        value = c.maxValue;
      }

      // Respeitar limite mínimo
      if (c.minValue && value < c.minValue) {
        value = c.minValue;
      }

      return {
        ...c,
        value: value,
        onEditValue: value,
        checked: defaultEnabled || c.checked
      };
    });
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          setFieldTouched,
          setFieldValue,
          submitForm,
          isSubmitting
        }) => {
          const onChangeCoverage = (field: string, valueMask: string): void => {
            hasChange();
            const onlyNumber = valueMask.replace(/\D/g, '');
            const value = Number(onlyNumber) / 100;

            setFieldValue(field, value);
          };

          const w100 = {
            width: '100%'
          };

          const columnDistance = {
            marginBottom: 20
          };

          return (
            <>
              <Dimmer active={isSubmitting} inverted page>
                <Loader indeterminate> Gerando orçamento... </Loader>
              </Dimmer>

              <Form>
                <S.Container>
                  <Grid style={w100}>
                    <Grid.Column
                      computer={8}
                      mobile={16}
                      tablet={16}
                      style={columnDistance}
                    >
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <S.Subtitle>Dados para cotação</S.Subtitle>
                          </Grid.Column>
                          <Grid.Column width={16}>
                            <S.Hr />
                          </Grid.Column>
                        </Grid.Row>

                        <Grid.Row>
                          <Grid.Column
                            computer={5}
                            mobile={5}
                            tablet={5}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>TIPO DE COBERTURA</FieldLabel>
                            <Dropdown
                              fluid
                              search
                              clearable
                              selection
                              name={'coverageType'}
                              placeholder={'Selecione o tipo de cobertura'}
                              options={buildingContentOptions}
                              disabled={true}
                              value={values.coverageType}
                              onChange={(_event, { value }) => {
                                hasChange();
                                setFieldValue('coverageType', value);
                                setFieldTouched('coverageType', false, false);
                              }}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={5}
                            mobile={5}
                            tablet={5}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>TIPO DO IMÓVEL</FieldLabel>
                            <Dropdown
                              fluid
                              search
                              clearable
                              selection
                              name={'propertyType'}
                              placeholder={'Selecione o tipo do imóvel'}
                              options={propertyTypeOptions}
                              value={values.propertyType}
                              onChange={(_event, { value }) => {
                                hasChange();
                                setFieldValue('propertyType', value);
                                setFieldTouched('propertyType', false, false);
                                setFieldValue('assistance', null);

                                // Limpar assistência selecionada ao mudar tipo de imóvel
                                setSelectedAssistance(null);

                                // Filtrar coberturas pelo tipo de imóvel selecionado
                                if (value) {
                                  const filteredCoverages = filterCoveragesByPropertyType(
                                    value as string
                                  );
                                  if (filteredCoverages) {
                                    setFieldValue('coverages', filteredCoverages);
                                  }
                                }
                              }}
                            />
                          </Grid.Column>

                          <Grid.Column
                            computer={6}
                            mobile={6}
                            tablet={6}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>
                              ASSISTÊNCIA{' '}
                              <Icon
                                name="info circle"
                                onClick={() => {
                                  setTypeAssistanceCoverageModal('assistance');
                                  setShowAssistanceCoverageInfoModal(true);
                                }}
                                style={{
                                  marginLeft: '8px',
                                  color: '#1D79BE',
                                  cursor: 'pointer'
                                }}
                              />
                            </FieldLabel>
                            <Dropdown
                              fluid
                              search
                              clearable
                              selection
                              name={'assistance'}
                              placeholder={'Selecione a assistência (opcional)'}
                              options={[
                                { key: 'none', text: 'SEM ASSISTÊNCIA', value: '-' },
                                ...assistanceCoverages.map((ac, idx) => ({
                                  key: ac.coverage.id,
                                  text: ac.coverage.name.trim(),
                                  value: String(ac.coverage.id)
                                }))
                              ]}
                              value={selectedAssistance || ''}
                              onChange={(_event, { value }) => {
                                hasChange();
                                console.log(
                                  '[Dropdown Assistência] Valor selecionado:',
                                  value
                                );

                                // Se value é '-', significa que "SEM ASSISTÊNCIA" foi selecionado
                                // Se value é null/undefined/empty, significa que foi limpo
                                if (value === '-') {
                                  setSelectedAssistance('-');
                                } else if (
                                  value === '' ||
                                  value === null ||
                                  value === undefined
                                ) {
                                  setSelectedAssistance(null);
                                } else {
                                  setSelectedAssistance(String(value));
                                }

                                console.log(
                                  '[Dropdown Assistência] selectedAssistance atualizado para:',
                                  value
                                );
                              }}
                            />
                          </Grid.Column>
                        </Grid.Row>

                        <Grid.Row>
                          <Grid.Column
                            computer={8}
                            mobile={8}
                            tablet={8}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>VALOR DO ALUGUEL</FieldLabel>
                            <Input
                              fluid
                              name={'rentValue'}
                              placeholder={'Valor do Aluguel'}
                              value={
                                values.rentValue
                                  ? numberToMoneyString(values.rentValue)
                                  : ''
                              }
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                hasChange();
                                onChangeCoverage('rentValue', event.target.value);
                                setFieldTouched('rentValue', false, false);
                              }}
                              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                                const onlyNumber = event.target.value.replace(/\D/g, '');
                                const rentValue = Number(onlyNumber) / 100;

                                if (rentValue > 0) {
                                  // Atualizar coberturas regulares
                                  if (values.coverages && values.coverages.length > 0) {
                                    const updatedCoverages = applyDefaultCoverageValues(
                                      values.coverages,
                                      rentValue
                                    );
                                    setFieldValue('coverages', updatedCoverages);
                                  }

                                  // Atualizar cobertura de incêndio baseado no tipo selecionado
                                  if (values.coverageType) {
                                    const fireCoverage = getFireCoverageByType(
                                      values.coverageType,
                                      values.propertyType
                                    );
                                    if (fireCoverage && fireCoverage.multiplier) {
                                      let fireValue = fireCoverage.multiplier * rentValue;

                                      // Respeitar limites
                                      if (
                                        fireCoverage.maxValue &&
                                        fireValue > fireCoverage.maxValue
                                      ) {
                                        fireValue = fireCoverage.maxValue;
                                      }
                                      if (
                                        fireCoverage.minValue &&
                                        fireValue < fireCoverage.minValue
                                      ) {
                                        fireValue = fireCoverage.minValue;
                                      }

                                      setFieldValue('fireInsuranceValue', fireValue);
                                    }
                                  }
                                }
                              }}
                              error={touched.rentValue && errors.rentValue === 'error'}
                            />
                          </Grid.Column>
                          <Grid.Column width={8}>
                            <FieldLabel paddingBottom>
                              PARCELAS
                              {configs?.flexibleInstallmentsEnabled && (
                                <Popup
                                  content={
                                    <div style={{ maxWidth: '300px', lineHeight: '1.4' }}>
                                      <p
                                        style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}
                                      >
                                        Informação sobre parcelamento:
                                      </p>
                                      <p style={{ margin: '0 0 8px 0' }}>
                                        O seguro será cobrado mensalmente pela seguradora
                                        (12x ao ano).
                                      </p>
                                      <p style={{ margin: '0' }}>
                                        Caso deseje trabalhar com parcelamentos diferentes
                                        para a imobiliária, você pode selecionar um número
                                        menor de parcelas. O valor será ajustado
                                        automaticamente e você poderá acompanhar pela sessão
                                        de conciliação no módulo de Faturas.
                                      </p>
                                    </div>
                                  }
                                  trigger={
                                    <Icon
                                      name="info circle"
                                      style={{
                                        marginLeft: '8px',
                                        color: '#1D79BE',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  }
                                  position="top center"
                                  size="small"
                                />
                              )}
                            </FieldLabel>
                            <Dropdown
                              fluid
                              selection
                              placeholder={'Número de Parcelas'}
                              value={values.installments}
                              options={installmentsOptions}
                              disabled={!configs?.flexibleInstallmentsEnabled}
                              onChange={(e, { value }) => {
                                setFieldValue('installments', value as number);
                              }}
                            />
                          </Grid.Column>
                        </Grid.Row>

                        <Grid.Row>
                          <Grid.Column
                            computer={8}
                            mobile={8}
                            tablet={8}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>
                              COMPETÊNCIA
                              <Popup
                                content={
                                  'A Yelum transmite sempre com mês cheio, com início no dia 1. Competência do mês atual entra no lote imediatamente. Competência do próximo mês será agendada e processada automaticamente no dia 1.'
                                }
                                position={'top left'}
                                trigger={
                                  <Icon
                                    name={'info circle'}
                                    color={'blue'}
                                    style={{ cursor: 'help', marginLeft: 5 }}
                                  />
                                }
                              />
                            </FieldLabel>
                            <Dropdown
                              fluid
                              selection
                              name={'competence'}
                              value={values.competence}
                              options={competenceOptions.map(option => ({
                                key: option.value,
                                text: option.label,
                                value: option.value,
                              }))}
                              onChange={(_, { value }) => {
                                const updated = applyYelumCompetenceToFormValues(
                                  value as YelumCompetence,
                                );
                                setFieldValue('competence', updated.competence);
                                setFieldValue('startVigency', updated.startVigency);
                                setFieldValue('endVigency', updated.endVigency);
                                setFieldTouched('competence', false, false);
                                if (step === 1) {
                                  setStep(0);
                                  setCalculationResult(null);
                                }
                              }}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                              {values.competence
                                ? getCompetenceFlowLabel(values.competence)
                                : null}
                            </div>
                            {!hasYelumPolicies &&
                              values.competence === YelumCompetence.CURRENT_MONTH && (
                                <div style={{ fontSize: 12, color: '#c35a00', marginTop: 6 }}>
                                  Se esta imobiliária ainda não possui apólices Yelum, a
                                  contratação do mês atual pode ser recusada. Nesse caso,
                                  selecione o próximo mês.
                                </div>
                              )}
                          </Grid.Column>

                          <Grid.Column
                            computer={8}
                            mobile={8}
                            tablet={8}
                            style={columnDistance}
                          >
                            <FieldLabel paddingBottom>FIM DA VIGÊNCIA</FieldLabel>
                            <S.DatePickerWrapper>
                              <CustomDatePicker
                                disabled={true}
                                name={'endVigency'}
                                dateFormat={'dd/MM/yyyy'}
                                placeholderText={'__/__/____'}
                                selected={values.endVigency ?? undefined}
                                customInput={
                                  <AppMaskedInput
                                    mask={DATE_INPUT_MASK}
                                    error={errors.endVigency === 'error'}
                                  />
                                }
                              />
                            </S.DatePickerWrapper>
                          </Grid.Column>
                        </Grid.Row>

                        {canEditPl && (
                        <div
                          style={{
                            margin: '20px 0',
                            alignItems: 'center',
                            width: '100%',
                            display: 'flex'
                          }}
                        >
                          <div
                            style={{ fontSize: 14, color: '#666', whiteSpace: 'nowrap' }}
                          >
                            *Opcional
                          </div>
                          <div
                            style={{
                              flexGrow: 1,
                              height: 1,
                              backgroundColor: '#dcdcdc',
                              margin: '0 10px'
                            }}
                          ></div>
                          <div
                            style={{
                              color: '#5f2dab',
                              fontSize: 14,
                              fontWeight: 800,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                            onClick={() =>
                              setIsConfiguracoesCoberturaExpanded(
                                !isConfiguracoesCoberturaExpanded
                              )
                            }
                          >
                            Configurações avançadas (%)
                            <i
                              className="icone"
                              style={{
                                border: '1px solid #5f2dab',
                                borderWidth: '0 2px 2px 0',
                                display: 'inline-block',
                                padding: 4,
                                transform: isConfiguracoesCoberturaExpanded
                                  ? 'rotate(-135deg)'
                                  : 'rotate(45deg)',
                                transition: 'transform 0.3s ease'
                              }}
                            ></i>
                          </div>

                        </div>
                        )}

                        {canEditPl && isConfiguracoesCoberturaExpanded && (
                          <>
                            <Grid.Row>
                              <Grid.Column
                                computer={12}
                                mobile={12}
                                tablet={12}
                                style={columnDistance}
                              >
                                <FieldLabel paddingBottom>PL(%)</FieldLabel>
                                <RangeSlider
                                  min={0}
                                  max={configs?.compensation}
                                  step={1}
                                  value={values.commissionTax || 0}
                                  onChange={(value: number) => {
                                    hasChange();
                                    setFieldValue('commissionTax', value);
                                  }}
                                />
                              </Grid.Column>

                              <Grid.Column
                                computer={4}
                                mobile={4}
                                tablet={4}
                                style={{
                                  ...columnDistance,
                                  marginTop: 10
                                }}
                              >
                                <Input
                                  fluid
                                  value={values.commissionTax}
                                  onChange={(_event, { value }) => {
                                    // if (
                                    //   Number(value) <
                                    //   Math.floor(configs?.compensation / 2)
                                    // ) {
                                    //   setFieldValue(
                                    //     'commissionTax',
                                    //     Math.floor(configs?.compensation / 2)
                                    //   );
                                    //   setFieldTouched('commissionTax', false, false);
                                    //   return;
                                    // }

                                    if (Number(value) > configs?.compensation) {
                                      setFieldValue(
                                        'commissionTax',
                                        configs?.compensation
                                      );
                                      setFieldTouched('commissionTax', false, false);
                                      return;
                                    }
                                    hasChange();
                                    setFieldValue('commissionTax', value);
                                    setFieldTouched('commissionTax', false, false);
                                  }}
                                  name={'commissionTax'}
                                  type="number"
                                  // min={Math.floor(configs?.compensation / 2)}
                                  min={0}
                                  max={configs?.compensation}
                                />
                              </Grid.Column>
                            </Grid.Row>

                            {/* {user?.role === UserRolesEnum.brokerAdmin ||
                              user?.role === UserRolesEnum.brokerAnalyst ? (
                              <Grid.Row>
                                <Grid.Column
                                  computer={12}
                                  mobile={12}
                                  tablet={12}
                                  style={columnDistance}
                                >
                                  <FieldLabel paddingBottom>Comissão(%)</FieldLabel>
                                  <RangeSlider
                                    min={Math.floor(configs?.brokerCommission / 2)} // metade do valor máximo da comissão
                                    max={configs?.brokerCommission}
                                    step={1}
                                    value={values.brokerCommission || 0}
                                    onChange={(value: number) => {
                                      hasChange();
                                      setFieldValue('brokerCommission', value);
                                    }}
                                  />
                                </Grid.Column>

                                <Grid.Column
                                  computer={4}
                                  mobile={4}
                                  tablet={4}
                                  style={{
                                    ...columnDistance,
                                    marginTop: 10
                                  }}
                                >
                                  <Input
                                    fluid
                                    value={values.brokerCommission}
                                    onChange={(_event, { value }) => {
                                      if (
                                        Number(value) <
                                        Math.floor(configs?.brokerCommission / 2)
                                      ) {
                                        setFieldValue(
                                          'brokerCommission',
                                          Math.floor(configs?.brokerCommission / 2)
                                        );
                                        setFieldTouched('brokerCommission', false, false);
                                        return;
                                      }

                                      if (Number(value) > configs?.brokerCommission) {
                                        setFieldValue(
                                          'brokerCommission',
                                          configs?.brokerCommission
                                        );
                                        setFieldTouched('brokerCommission', false, false);
                                        return;
                                      }
                                      hasChange();
                                      setFieldValue('brokerCommission', value);
                                      setFieldTouched('brokerCommission', false, false);
                                    }}
                                    name={'brokerCommission'}
                                    type="number"
                                    min={Math.floor(configs?.brokerCommission / 2)}
                                    max={configs?.brokerCommission}
                                  />
                                </Grid.Column>
                              </Grid.Row>
                            ) : null} */}
                          </>
                        )}
                      </Grid>
                    </Grid.Column>
                    <Grid.Column
                      computer={8}
                      mobile={16}
                      tablet={16}
                      style={columnDistance}
                    >
                      <Sticky offset={80}>
                        <S.PreQuoteWrapper>
                          <S.PreQuoteHeader>
                            <span className="left">
                              <Icon name="shield" />
                              Orçamento de Seguro{' '}
                              <Icon
                                name="info circle"
                                onClick={() => {
                                  setTypeAssistanceCoverageModal('coverage');
                                  setShowAssistanceCoverageInfoModal(true);
                                }}
                                style={{
                                  marginLeft: '10px',
                                  marginTop: '5px',
                                  color: '#FFFFFF',
                                  cursor: 'pointer'
                                }}
                              />
                            </span>
                          </S.PreQuoteHeader>

                          <Grid style={{ height: 'calc(100% - 60px)', margin: 0 }}>
                            <S.SecctionName>Coberturas disponíveis</S.SecctionName>
                          </Grid>

                          {/* Cobertura de Incêndio (sempre primeiro e obrigatório) */}
                          <S.CoveragesRow>
                            <div style={{ flex: 1 }}>
                              <span
                                className={'full-insurance-name'}
                                style={{ color: '#000000' }}
                              >
                                <input
                                  type="checkbox"
                                  checked={true}
                                  disabled={true}
                                  style={{ cursor: 'not-allowed' }}
                                />
                                INCÊNDIO / EXPLOSÃO / FUMAÇA / Q. AERONAVES
                                <span
                                  style={{
                                    color: '#ff6b6b',
                                    fontSize: '12px',
                                    marginLeft: '5px'
                                  }}
                                >
                                  (Obrigatória)
                                </span>
                              </span>
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '2px'
                              }}
                            >
                              <Input
                                placeholder="R$ 0,00"
                                value={
                                  values.fireInsuranceValue
                                    ? numberToMoneyString(+values.fireInsuranceValue)
                                    : ''
                                }
                                onChange={event => {
                                  hasChange();
                                  const onlyNumber = event.target.value.replace(
                                    /\D/g,
                                    ''
                                  );
                                  const value = Number(onlyNumber) / 100;
                                  setFieldValue('fireInsuranceValue', value);
                                  setFieldTouched('fireInsuranceValue', false, false);
                                }}
                                style={{
                                  width: '150px',
                                  textAlign: 'right'
                                }}
                              />
                              {(() => {
                                const fireCoverage = getFireCoverageByType(
                                  values.coverageType,
                                  values.propertyType
                                );
                                const propertyLimits =
                                  YELUM_PROPERTY_LIMITS[values.propertyType];
                                if (
                                  fireCoverage &&
                                  propertyLimits &&
                                  fireCoverage.coverage
                                ) {
                                  const coverageLabel = getCoverageLabelFromCoverage(
                                    fireCoverage.coverage
                                  );
                                  if (coverageLabel) {
                                    const fireLimit = getLimitByCoverageLike(
                                      coverageLabel,
                                      propertyLimits
                                    );
                                    if (
                                      fireLimit &&
                                      values.fireInsuranceValue > fireLimit
                                    ) {
                                      return (
                                        <span
                                          style={{
                                            color: '#ff6b6b',
                                            fontSize: '10px',
                                            textAlign: 'right',
                                            maxWidth: '150px'
                                          }}
                                        >
                                          Máx: {numberToMoneyString(fireLimit)}
                                        </span>
                                      );
                                    }
                                  }
                                }
                                return null;
                              })()}
                            </div>
                          </S.CoveragesRow>

                          {values.coverages
                            ?.slice(0, showAllCoverages ? values.coverages.length : 8)
                            .map((c: LoadCoveragesResponseControl, index: number) => {
                              return (
                                <S.CoveragesRow key={c.id}>
                                  <div style={{ flex: 1 }}>
                                    <span
                                      className={'full-insurance-name'}
                                      style={{
                                        color: c.checked ? '#000000' : '#A3A3A3'
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        onClick={() => {
                                          // Não permitir desmarcar coberturas obrigatórias
                                          if (c.isRequired) {
                                            return;
                                          }

                                          const updatedCoverages = [...values.coverages];
                                          updatedCoverages[index] = {
                                            ...updatedCoverages[index],
                                            checked: !updatedCoverages[index].checked
                                          };
                                          setFieldValue('coverages', updatedCoverages);
                                          hasChange();
                                        }}
                                        checked={c.checked || false}
                                        disabled={c.isRequired}
                                        style={{
                                          cursor: c.isRequired ? 'not-allowed' : 'pointer'
                                        }}
                                      />
                                      {c.coverage.name}
                                      {c.isRequired && (
                                        <span
                                          style={{
                                            color: '#ff6b6b',
                                            fontSize: '12px',
                                            marginLeft: '5px'
                                          }}
                                        >
                                          (Obrigatória)
                                        </span>
                                      )}
                                    </span>
                                  </div>

                                  <div>
                                    {!c.onEdit ? (
                                      <div className="noEditContainer">
                                        <div className="noEdit">
                                          <div
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-end'
                                            }}
                                          >
                                            <span
                                              className="insurance-value"
                                              style={{
                                                color: c.checked ? '#2185d0' : '#A3A3A3'
                                              }}
                                            >
                                              {c.value
                                                ? numberToMoneyString(c.value)
                                                : ''}
                                            </span>
                                            {(() => {
                                              const propertyLimits =
                                                YELUM_PROPERTY_LIMITS[
                                                  values.propertyType
                                                ];
                                              if (
                                                propertyLimits &&
                                                c.coverage &&
                                                c.value > 0
                                              ) {
                                                const coverageLabel =
                                                  getCoverageLabelFromCoverage(
                                                    c.coverage
                                                  );
                                                if (coverageLabel) {
                                                  const limit = getLimitByCoverageLike(
                                                    coverageLabel,
                                                    propertyLimits
                                                  );
                                                  if (limit && c.value > limit) {
                                                    return (
                                                      <span
                                                        style={{
                                                          color: '#ff6b6b',
                                                          fontSize: '10px',
                                                          marginTop: '2px'
                                                        }}
                                                      >
                                                        Máx: {numberToMoneyString(limit)}
                                                      </span>
                                                    );
                                                  }
                                                }
                                              }
                                              return null;
                                            })()}
                                          </div>
                                        </div>
                                        <Icon
                                          name="pencil"
                                          className={
                                            'low-opacity  pencil-icon ' +
                                            (c.value > 0 ? 'marginTop' : '')
                                          }
                                          onClick={() => {
                                            setIsEditing(true);

                                            const updatedCoverages = [
                                              ...values.coverages
                                            ];
                                            updatedCoverages[index] = {
                                              ...updatedCoverages[index],
                                              onEdit: true,
                                              onEditValue: updatedCoverages[index].value
                                            };
                                            setFieldValue('coverages', updatedCoverages);
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        />
                                      </div>
                                    ) : (
                                      <div className="editable-coverages">
                                        <div
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end'
                                          }}
                                        >
                                          <Input
                                            fluid
                                            className="editable-coverages-input"
                                            placeholder="Digite o valor da cobertura"
                                            value={
                                              c.onEditValue
                                                ? numberToMoneyString(+c.onEditValue)
                                                : ''
                                            }
                                            onChange={event => {
                                              const onlyNumber =
                                                event.target.value.replace(/\D/g, '');
                                              const value = Number(onlyNumber) / 100;

                                              // Atualizar Formik
                                              const updatedCoverages = [
                                                ...values.coverages
                                              ];
                                              updatedCoverages[index] = {
                                                ...updatedCoverages[index],
                                                onEditValue: value
                                              };
                                              setFieldValue(
                                                'coverages',
                                                updatedCoverages
                                              );
                                            }}
                                            onKeyPress={(
                                              e: React.KeyboardEvent<HTMLInputElement>
                                            ) => {
                                              if (e.key === 'Enter') {
                                                // Confirmar edição com Enter
                                                const propertyLimits =
                                                  YELUM_PROPERTY_LIMITS[
                                                    values.propertyType
                                                  ];
                                                let limit: number | undefined = undefined;

                                                if (propertyLimits && c.coverage) {
                                                  const coverageLabel =
                                                    getCoverageLabelFromCoverage(
                                                      c.coverage
                                                    );
                                                  if (coverageLabel) {
                                                    limit = getLimitByCoverageLike(
                                                      coverageLabel,
                                                      propertyLimits
                                                    );
                                                  }
                                                }

                                                if (c.onEditValue > 0) {
                                                  if (limit && c.onEditValue > limit) {
                                                    warningNotification(
                                                      'Valor excede o limite',
                                                      `O valor informado (R$ ${numberToMoneyString(
                                                        c.onEditValue
                                                      )}) excede o limite máximo de R$ ${numberToMoneyString(
                                                        limit
                                                      )} para esta cobertura.`
                                                    );
                                                    return;
                                                  }
                                                  const updatedCoverages = [
                                                    ...values.coverages
                                                  ];
                                                  updatedCoverages[index] = {
                                                    ...updatedCoverages[index],
                                                    value:
                                                      updatedCoverages[index].onEditValue,
                                                    onEdit: false
                                                  };
                                                  setFieldValue(
                                                    'coverages',
                                                    updatedCoverages
                                                  );
                                                  setIsEditing(false);
                                                }
                                              }
                                            }}
                                          />
                                          {(() => {
                                            const propertyLimits =
                                              YELUM_PROPERTY_LIMITS[values.propertyType];
                                            const currentValue = c.onEdit
                                              ? c.onEditValue
                                              : c.value;
                                            if (propertyLimits && c.coverage) {
                                              const coverageLabel =
                                                getCoverageLabelFromCoverage(c.coverage);
                                              if (coverageLabel) {
                                                const limit = getLimitByCoverageLike(
                                                  coverageLabel,
                                                  propertyLimits
                                                );
                                                if (limit && currentValue > limit) {
                                                  return (
                                                    <span
                                                      style={{
                                                        color: '#ff6b6b',
                                                        fontSize: '10px',
                                                        marginTop: '2px'
                                                      }}
                                                    >
                                                      Máx: {numberToMoneyString(limit)}
                                                    </span>
                                                  );
                                                }
                                              }
                                            }
                                            return null;
                                          })()}
                                        </div>
                                        <Icon
                                          className="icon"
                                          name="check"
                                          color="green"
                                          onClick={() => {
                                            const propertyLimits =
                                              YELUM_PROPERTY_LIMITS[values.propertyType];
                                            let limit: number | undefined = undefined;

                                            if (propertyLimits && c.coverage) {
                                              const coverageLabel =
                                                getCoverageLabelFromCoverage(c.coverage);
                                              if (coverageLabel) {
                                                limit = getLimitByCoverageLike(
                                                  coverageLabel,
                                                  propertyLimits
                                                );
                                              }
                                            }

                                            if (c.onEditValue > 0) {
                                              if (limit && c.onEditValue > limit) {
                                                warningNotification(
                                                  'Valor excede o limite',
                                                  `O valor informado (R$ ${numberToMoneyString(
                                                    c.onEditValue
                                                  )}) excede o limite máximo de R$ ${numberToMoneyString(
                                                    limit
                                                  )} para esta cobertura.`
                                                );
                                                return;
                                              }
                                              const updatedCoverages = [
                                                ...values.coverages
                                              ];
                                              updatedCoverages[index] = {
                                                ...updatedCoverages[index],
                                                value:
                                                  updatedCoverages[index].onEditValue,
                                                onEdit: false
                                              };
                                              setFieldValue(
                                                'coverages',
                                                updatedCoverages
                                              );
                                              setIsEditing(false);
                                            } else {
                                              warningNotification(
                                                'Valor inválido',
                                                'Por favor, insira um valor maior que zero para a cobertura.'
                                              );
                                            }
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        />
                                        <Icon
                                          className="icon"
                                          name="close"
                                          color="red"
                                          onClick={() => {
                                            // Cancelar edição
                                            const updatedCoverages = [
                                              ...values.coverages
                                            ];
                                            updatedCoverages[index] = {
                                              ...updatedCoverages[index],
                                              onEdit: false,
                                              onEditValue: updatedCoverages[index].value
                                            };
                                            setFieldValue('coverages', updatedCoverages);
                                            setIsEditing(false);
                                          }}
                                          style={{
                                            cursor: 'pointer',
                                            marginLeft: '5px'
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </S.CoveragesRow>
                              );
                            })}

                          {/* Botão Ver mais/Ver menos */}
                          {values.coverages && values.coverages.length > 8 && (
                            <S.CoveragesRow
                              style={{
                                justifyContent: 'center',
                                marginTop: '10px',
                                marginBottom: '10px'
                              }}
                            >
                              <Button
                                basic
                                size="small"
                                onClick={() => setShowAllCoverages(!showAllCoverages)}
                                style={{
                                  borderColor: '#59249B',
                                  fontSize: '12px',
                                  padding: '8px 16px'
                                }}
                              >
                                <span style={{ color: '#59249B' }}>
                                  <Icon
                                    name={
                                      showAllCoverages ? 'chevron up' : 'chevron down'
                                    }
                                  />
                                  {showAllCoverages
                                    ? 'Ver Menos'
                                    : `Ver Mais (${
                                        values.coverages.length - 8
                                      } restantes)`}
                                </span>
                              </Button>
                            </S.CoveragesRow>
                          )}

                          {step === 1 && calculationResult && (
                            <>
                              <S.Hr style={w100} />
                              <Grid style={{ paddingLeft: 15 }}>
                                <Grid.Row style={{ paddingLeft: 15 }}>
                                  <Grid.Column
                                    computer={10}
                                    mobile={10}
                                    tablet={10}
                                    style={columnDistance}
                                  >
                                    <FieldLabel paddingBottom>
                                      <span
                                        style={{
                                          color: '#1D79BE'
                                        }}
                                      >
                                        Seu orçamento
                                      </span>
                                    </FieldLabel>
                                    <FieldLabel
                                      paddingBottom
                                      style={{
                                        color: '#1D79BE'
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 800,
                                          fontSize: 22
                                        }}
                                      >
                                        {numberToMoneyString(
                                          calculationResult.totalMonthly
                                        )}
                                        <span
                                          style={{
                                            fontSize: 12,
                                            color: '#1D79BE'
                                          }}
                                        >
                                          {' '}
                                          /Mês{' '}
                                        </span>
                                      </span>
                                    </FieldLabel>
                                    {values.installments !== 12 && (
                                      <FieldLabel
                                        paddingBottom
                                        style={{
                                          color: '#1D79BE',
                                          marginTop: 8
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontWeight: 600,
                                            fontSize: 16
                                          }}
                                        >
                                          Valor Parcelamento: {values.installments}x{' '}
                                          {numberToMoneyString(
                                            Number(
                                              (
                                                (calculationResult.totalMonthly * 12) /
                                                values.installments
                                              ).toFixed(2)
                                            )
                                          )}
                                        </span>
                                      </FieldLabel>
                                    )}
                                  </Grid.Column>

                                  <Grid.Column
                                    computer={5}
                                    mobile={5}
                                    tablet={5}
                                    style={columnDistance}
                                  >
                                    <Popup
                                      content={'Salvar orçamento'}
                                      trigger={
                                        <Button
                                          basic
                                          icon
                                          color="blue"
                                          fluid
                                          onClick={() => {
                                            const assistanceName = selectedAssistance
                                              ? selectedAssistance === ''
                                                ? 'SEM ASSISTÊNCIA'
                                                : assistanceCoverages.find(
                                                  ac =>
                                                    String(ac.coverage.id) ===
                                                    String(selectedAssistance)
                                                )?.coverage.name || 'SEM ASSISTÊNCIA'
                                              : 'SEM ASSISTÊNCIA';

                                            const combinedData = {
                                              ...data,
                                              ...values,
                                              calculationResult,
                                              coverageType: values.coverageType,
                                              propertyType: values.propertyType,
                                              rentValue: values.rentValue,
                                              competence: values.competence,
                                              startVigency: values.startVigency,
                                              endVigency: values.endVigency,
                                              assistance: assistanceName,
                                              assistanceId:
                                                selectedAssistance &&
                                                selectedAssistance !== ''
                                                  ? selectedAssistance
                                                  : null,
                                              commissionTax: values.commissionTax,
                                              brokerCommission: values.brokerCommission,
                                              coverages: values.coverages,
                                              fireInsuranceValue:
                                                values.fireInsuranceValue,
                                              allCoverages: allCoverages,
                                              discount: values.discount,
                                              hasDiscount,
                                              policyPeriodStart: values.startVigency,
                                              policyPeriodEnd: values.endVigency,
                                              totalPremium:
                                                hasDiscount && values.discount
                                                  ? calculationResult.totalAnnual -
                                                    (calculationResult.totalAnnual *
                                                      values.discount) /
                                                      100
                                                  : calculationResult.totalAnnual,
                                              yelumMonthlyValue:
                                                calculationResult.totalMonthly,
                                              clientInstallments: values.installments,
                                              clientInstallmentValue:
                                                (calculationResult.totalMonthly * 12) /
                                                values.installments
                                            };

                                            const modalData =
                                              isEdit && budgetLoadedData
                                                ? {
                                                    ...budgetLoadedData,
                                                    ...combinedData,
                                                    isEdit: true,
                                                    context: context
                                                  }
                                                : combinedData;
                                            setSavedData(modalData);
                                            setOpenCreatePolicyModal(true);
                                          }}
                                          disabled={isEditing}
                                        >
                                          <Icon name={'save'} /> Salvar orçamento
                                        </Button>
                                      }
                                    />

                                    {canContract && <Popup
                                      content={'Contratar seguro'}
                                      trigger={
                                        <Button
                                          icon
                                          color="blue"
                                          fluid
                                          style={{ marginTop: 8 }}
                                          onClick={() => {
                                            const assistanceName = selectedAssistance
                                              ? selectedAssistance === ''
                                                ? 'SEM ASSISTÊNCIA'
                                                : assistanceCoverages.find(
                                                  ac =>
                                                    ac.coverage.id ===
                                                    selectedAssistance
                                                )?.coverage.name || 'SEM ASSISTÊNCIA'
                                              : 'SEM ASSISTÊNCIA';

                                            const combinedData = {
                                              ...data,
                                              ...values,
                                              calculationResult,
                                              coverageType: values.coverageType,
                                              propertyType: values.propertyType,
                                              rentValue: values.rentValue,
                                              competence: values.competence,
                                              startVigency: values.startVigency,
                                              endVigency: values.endVigency,
                                              assistance: assistanceName,
                                              assistanceId:
                                                selectedAssistance &&
                                                  selectedAssistance !== ''
                                                  ? selectedAssistance
                                                  : null,
                                              commissionTax: values.commissionTax,
                                              brokerCommission: values.brokerCommission,
                                              coverages: values.coverages,
                                              fireInsuranceValue:
                                                values.fireInsuranceValue,
                                              allCoverages: allCoverages,
                                              discount: values.discount,
                                              hasDiscount,
                                              policyPeriodStart: values.startVigency,
                                              policyPeriodEnd: values.endVigency,
                                              totalPremium:
                                                hasDiscount && values.discount
                                                  ? calculationResult.totalAnnual -
                                                  (calculationResult.totalAnnual *
                                                    values.discount) /
                                                  100
                                                  : calculationResult.totalAnnual,
                                              yelumMonthlyValue:
                                                calculationResult.totalMonthly,
                                              clientInstallments: values.installments,
                                              clientInstallmentValue:
                                                (calculationResult.totalMonthly * 12) /
                                                values.installments
                                            };

                                            const modalData =
                                              isEdit && budgetLoadedData
                                                ? {
                                                  ...budgetLoadedData,
                                                  ...combinedData,
                                                  isEdit: true,
                                                  context: context
                                                }
                                                : combinedData;
                                            setSavedData(modalData);
                                            setOpenCreatePolicyModal(true);
                                          }}
                                          disabled={isEditing}
                                        >
                                          <Icon name={'handshake'} />
                                          <span style={{ marginLeft: 8 }}>
                                            Contratar
                                          </span>
                                        </Button>
                                      }
                                    />}
                                  </Grid.Column>
                                </Grid.Row>
                              </Grid>
                            </>
                          )}

                          {step === 0 && (
                            <S.Summarysection>
                              <div className="buttons">
                                <Button
                                  onClick={() => {
                                    if (
                                      coveragesValidation(
                                        values.coverages,
                                        values.propertyType,
                                        values.fireInsuranceValue,
                                        values.coverageType
                                      )
                                    ) {
                                      setFieldValue('discount', 0);
                                      setHasDiscount(false);
                                      return submitForm();
                                    }
                                  }}
                                >
                                  <Icon name="clipboard check" />
                                  {'Criar Orçamento'}
                                </Button>
                              </div>
                            </S.Summarysection>
                          )}
                        </S.PreQuoteWrapper>
                      </Sticky>
                    </Grid.Column>
                  </Grid>
                </S.Container>
              </Form>
            </>
          );
        }}
      </Formik>
      {openCreatePolicyModal && (
        <YelumCreatePolicyModal
          open={openCreatePolicyModal}
          onClose={() => setOpenCreatePolicyModal(false)}
          data={savedData || data}
          estateId={estate?.id}
          initialAddress={initialAddress}
          initialTenant={initialTenant}
          initialInsured={initialInsured}
          context={context}
          fireInsuranceAnalysisId={budgetId}
        />
      )}
      {showAssistanceCoverageInfoModal && (
        <AssistanceAndCoverageDetailsModal
          open={showAssistanceCoverageInfoModal}
          type={typeAssistanceCoverageModal}
          onClose={() => setShowAssistanceCoverageInfoModal(false)}
        />
      )}
    </>
  );
};

const mapState = (state: iRootState) => {};

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateCurrentPage: (state: CurrentPageState) =>
    dispatch.currentPage.updateCurrentPage(state)
});

export default connect(mapState, mapDispatch)(YelumSeguroBudgetForm);
